import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { webcomponent } from '@adornis/functional-lit/webcomponent.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { Person } from '../../../users/entities/person.js';
import { InterviewSlot } from '../entities/InterviewSlot.js';

const statusBadge = (hasInterviewer: boolean, hasStudent: boolean) => {
  if (hasInterviewer && hasStudent) {
    return html`
      <div class="badge badge-success badge-sm">
        <span class="icon-[material-symbols--check-circle-rounded] size-4"></span>
        Termin vollständig
      </div>
    `;
  }

  if (hasInterviewer) {
    return html`
      <div class="badge badge-warning badge-sm">
        <span class="icon-[material-symbols--pending-rounded] size-4"></span>
        SuS hat diesen Termin noch nicht gebucht
      </div>
    `;
  }

  return html`
    <div class="badge badge-error badge-sm">
      <span class="icon-[material-symbols--pending-rounded] size-4"></span>
      Kein Gesprächspartner zugewiesen
    </div>
  `;
};

const renderSingleInterview = stateful(
  (props: { slot: InterviewSlot; selected: boolean; onSelect: (slot: InterviewSlot) => void }) => {
    const { value: child, loading: childLoading } = useQuery(
      () =>
        subscribeByID(
          Person,
          props.slot.childID,
        )({ _id: 1, debitorennummer: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } }),
      [props.slot.childID],
    );

    if (childLoading) {
      return html`
        <div class="card p-4">
          <div class="flex items-center justify-center">
            <span class="loading loading-spinner"></span>
          </div>
        </div>
      `;
    }

    const hasInterviewer = !!props.slot.interviewerID;
    const hasStudent = !!props.slot.childID;

    return html`
      <div
        class="card p-4 ${
          hasInterviewer && !props.selected
            ? 'opacity-50 cursor-not-allowed'
            : !props.selected
              ? 'border-success border-2 cursor-pointer bg-success/10'
              : 'border-primary border-2 bg-primary/10'
        }"
        @click=${() => {
          if (hasInterviewer) return;
          props.onSelect(props.slot);
        }}
      >
        <div class="flex flex-col gap-4">
          <!-- Time Display -->
          <div class="flex items-center justify-between gap-2">
            <div class="badge badge-primary font-medium">
              <span class="icon-[material-symbols--calendar-clock-rounded] size-4"></span>
              <span>
                ${props.slot.startDate ? format(props.slot.startDate, 'HH:mm') : ''} -
                ${props.slot.endDate ? format(props.slot.endDate, 'HH:mm') : ''}
              </span>
            </div>
          </div>

          <!-- Interviewer Section -->
          <div class="grid grid-cols-[1fr_2fr] items-center gap-3">
            <div class="flex gap-2 ">
              <span
                class="icon-[material-symbols--person-rounded] size-5 ${hasInterviewer ? 'text-success' : 'text-error'}"
              ></span>
              <span class="text-sm font-semibold text-base-content/70">Gesprächspartner:</span>
            </div>
            ${
              hasInterviewer
                ? html`
                    <div
                      class="badge badge-success badge-outline cursor-pointer"
                      @click=${() => {
                        goTo(`/personen/${child?._id}`);
                      }}
                    >
                      <span class="icon-[material-symbols--check-rounded] size-4"></span>
                      ${props.selected ? 'Ich' : 'Zugewiesen'}
                    </div>
                  `
                : html`
                    <div class="badge badge-ghost">
                      <span class="icon-[material-symbols--person-add-rounded] size-4"></span>
                      Verfügbar
                    </div>
                  `
            }

            <!-- Student Status -->
            <div class="flex gap-2 ">
              <span class="icon-[material-symbols--school-rounded] size-5"></span>
              <span class="text-sm font-semibold text-base-content/70">SuS:</span>
            </div>
            ${
              hasStudent
                ? html`
                    <div
                      class="badge badge-success badge-outline cursor-pointer"
                      @click=${() => {
                        goTo(`/personen/${child?._id}`);
                      }}
                    >
                      <span class="icon-[material-symbols--check-rounded] size-4"></span>
                      Zugewiesen
                    </div>
                  `
                : html`
                    <div class="badge badge-ghost">
                      <span class="icon-[material-symbols--person-add-rounded] size-4"></span>
                      Verfügbar
                    </div>
                  `
            }
          </div>

          <!-- Status Indicator -->

          <div class="flex justify-between items-center gap-4">
            <div class="text-sm text-base-content/70">Termin-ID: ${props.slot._id}</div>
            <div class="flex items-center gap-1">${statusBadge(hasInterviewer, hasStudent)}</div>
          </div>
        </div>
      </div>
    `;
  },
);

const generatorOptionsComponent = webcomponent(
  'sg-interview-teacher-choose-slot-dialog',
  (props: { timeSlots: InterviewSlot[]; resolve: () => void }) => {
    const user = useObservable(() => currentUser(), []);

    useQuery(
      () =>
        subscribeByID(
          InterviewSlot,
          props.timeSlots.map(slot => slot._id),
        )({ _id: 1 }),
      [],
    );

    const [manualUpdate, setManualUpdate] = useState(false);
    const update = () => setManualUpdate(!manualUpdate);

    if (!props.timeSlots.length)
      return html`<div class="flex items-center justify-center p-4">
        <span class="loading loading-spinner"></span>
      </div>`;

    const firstStart = props.timeSlots[0]?.startDate;
    const firstEnd = props.timeSlots[0]?.endDate;

    return html`
      <div class="flex flex-col p-8 gap-6 max-h-[90vh] overflow-y-auto">
        ${DialogHeader({
          title: html`Termine am ${firstStart ? format(firstStart, 'EEEE', { locale: de }) : ''} den
          ${firstStart ? format(firstStart, 'dd.MM.yyyy') : ''} um ${firstStart ? format(firstStart, 'HH:mm') : ''} -
          ${firstEnd ? format(firstEnd, 'HH:mm') : ''}`,
          close: () => props.resolve(),
          closeLabel: 'Dialog schließen',
        })}
        <div class="grid gap-4">
          ${repeat(
            props.timeSlots,
            slot => slot._id,
            slot =>
              renderSingleInterview({
                slot,
                selected: user?._id === slot.interviewerID,
                onSelect: async () => {
                  // the user can only select a single slot so unassign all other slots
                  props.timeSlots.forEach(async s => {
                    if (s.interviewerID === user?._id) {
                      s.interviewerID = null;
                      await s.save();
                    }
                  });

                  slot.interviewerID = user?._id;
                  await slot.save();

                  void SgSnackbar.show('Terminauswahl gespeichert');

                  update();
                },
              }),
          )}
        </div>
      </div>
    `;
  },
  {
    shadowRoot: true,
  },
);

export async function sgInterviewTeacherChooseSlot(timeSlots: InterviewSlot[]) {
  // ask the user for the specifics
  await showDialog<void>({
    content: resolve => generatorOptionsComponent.render({ resolve, timeSlots }),
  });
}
